import React, { useMemo, useState, useEffect } from 'react';
import styled from 'styled-components/macro';
import { Helmet } from 'react-helmet-async';
import Stack from '@mui/material/Stack';
import { useAuth } from '../../services/contexts/AuthContext';
import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../../Middleware/api/api';
import {
  Button,
  Divider as MuiDivider,
  Grid,
  Menu,
  MenuItem,
  Paper as MuiPaper,
  ThemeProvider,
  Typography,
  TableCell,
} from '@mui/material';
import { Add as AddIcon, ArrowForward } from '@mui/icons-material';
import { spacing } from '@mui/system';
import NutzerAnlegen from './components/CreateUser';
import { createTheme } from '@mui/material/styles';
import shadows from '@mui/material/styles/shadows';
import DataTable from '../../components/DataTable';
import StepperPopup from '../../components/StepperPopup';
import MultipleBillingStatusEdit from '../../components/multipleEditDialog/MultipleBillingStatusEdit';
import Feedback from '../../components/DataTable/Feedback';
import LogoLoader from '../../components/LogoLoader';
import { PAGE_SIZE } from '../../services/utils/units-constants';
import { getUserTypeData } from '../../services/utils/translations-constants.js';
import { useDropdown } from '../../services/hooks/DropDownStateHooks';
import { display } from '@mui/system';
const Divider = styled(MuiDivider)(spacing);

const Paper = styled(MuiPaper)(spacing);

const dataTableTheme = (appTheme) =>
  createTheme({
    components: {
      MuiList: {
        styleOverrides: {
          root: {
            maxHeight: 300,
          },
        },
      },
      MuiPaper: {
        variants: [
          {
            props: {
              variant: 'elevation',
            },
            style: {
              boxShadow: shadows[3],
            },
          },
        ],
      },
      MUIDataTableFilter: {
        styleOverrides: {
          root: {
            width: 300,
            boxShadow: shadows[3],
            backgroundColor: appTheme.palette.background.paper,
          },
          title: {
            lineHeight: 3,
          },
          header: {
            marginBottom: 0,
          },
          gridListTile: {
            styleOverrides: {
              paddingLeft: '25px',
              marginLeft: '25px',
            },
            sx: {
              pl: 28,
            },
          },

          reset: {
            display: 'flex',
            justifyContent: 'space-between',
            width: '100%',
            paddingRight: 4,
          },
          resetLink: {
            button: {
              variants: [
                {
                  props: {
                    variant: 'outlined',
                  },
                },
              ],
            },
          },
        },
      },
      MUIDataTableHeadCell: {
        styleOverrides: {
          root: {
            paddingLeft: '8px',
            '&[data-colindex="5"], &[data-colindex="8"]': {
              '& > *': {
                display: 'flex',
                justifyContent: 'center',
                textAlign: 'center',
              },
              '& .MuiButton-root': {
                margin: 'auto',
              },
              '&[data-colindex="4"], &[data-colindex="1"], &[data-colindex="2"], &[data-colindex="3"], &[data-colindex="4", &[data-colindex="6"]':
                {
                  '& > *': {
                    display: 'flex',
                    justifyContent: 'left',
                    textAlign: 'left',
                  },
                  '& .MuiButton-root': {
                    margin: 'auto',
                    paddingLeft: '8px',
                  },
                },
              '&[data-colindex="7"]': {
                '& > *': {
                  display: 'flex',
                  justifyContent: 'right',
                  textAlign: 'right',
                },
                '& .MuiButton-root': {
                  margin: 'auto',
                },
              },
            },
          },
        },
      },
      MuiFormControl: {
        defaultProps: {
          variant: 'filled',
          margin: 'dense',
          size: 'medium',
          fullWidth: false,
          sx: {
            ml: 2,
            width: '230px',
          },
        },
      },
      MuiSelect: {
        defaultProps: {
          margin: 'dense',
        },
      },
    },
  });
function EnhancedTable({ apiCallCount, handleReset }) {
  const { i18n, t } = useTranslation();
  const [dropDownForCpoData, setDropDownForCpoData] = useState([]);
  const [userTypeMap, setUserTypeMap] = useState(
    getUserTypeData(t).userTypeMap
  );
  const [selectedRows, setSelectedRows] = useState([]);
  const [selected, setSelected] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);
  const [pageSize, setPageSize] = useState(PAGE_SIZE[0]);
  const [fetchingData, setFetchingData] = React.useState(true);
  const [operatorContract, setOperatorContract] = React.useState([]);
  const { token } = useAuth();
  const [newFilters, setNewFilters] = useState([]);
  const {
    list: userTypesForCpoList,
    map: userTypesForCpoMap,
    setDropdown: setUserTypes,
  } = useDropdown();
  const [enterKeyboardButtonPressed, setEnterKeyboardButtonPressed] =
    useState(false);
  const buildUrlParams = useMemo(() => {
    const urlParams = new URLSearchParams();
    if (newFilters && typeof newFilters === 'object') {
      Object.entries(newFilters).forEach(([key, values]) => {
        if (Array.isArray(values) && values.length > 0) {
          let formattedValues = values;
          if (key === 'userTypes') {
            formattedValues = values.map(
              (label) => userTypesForCpoMap[label] || label
            );
          }
          if (key === 'locationIds') {
            formattedValues = values.map((val) => val.split(':')[0].trim());
          }
          urlParams.set(key, formattedValues.join(','));
        }
      });
    }

    urlParams.set('page', currentPage.toString());
    urlParams.set('size', pageSize.toString());
    return urlParams;
  }, [newFilters, currentPage, pageSize]);

  const transformRows = (rows) =>
    rows.map((row) => ({
      ...row,
      details: row,
      contractStatus: row.contractStatus,
      locationName: row.location,
      billingStatus:
        row.billingStatus === 'PAID'
          ? t('LBLBillPaid')
          : row.billingStatus === 'UNPAID'
            ? t('LBLBillUnpaid')
            : null,
    }));

  const handleApiResponse = (response) => {
    const data = response?.data?.data;
    if (!data) {
      setOperatorContract([]);
      return;
    }
    setTotalRecords(data.totalNumberOfRecords || 0);
    const rowsWithDetail = transformRows(data.response || []);
    setOperatorContract(rowsWithDetail);
  };

  const getUserData = () => {
    fetchContracts(buildUrlParams);
  };
  useEffect(() => {
    if (apiCallCount > 0) {
      getUserData();
    }
  }, [apiCallCount]);

  useEffect(() => {
    getUserData();
  }, [currentPage, pageSize]);

  const fetchContracts = async (params) => {
    setFetchingData(true);
    try {
      const response = await api.fetchOperatorContracts(token, params);
      handleApiResponse(response);
    } catch (error) {
      setOperatorContract([]);
    } finally {
      setFetchingData(false);
    }
  };

  useEffect(() => {
    if (token) {
      fetchConfigurationsDropDownForCpo();
    }
  }, [token]);

  const fetchConfigurationsDropDownForCpo = async () => {
    try {
      const urlParams = new URLSearchParams();
      urlParams.set('mappingTypes', 'USER_TYPE');
      const response = await api.fetchConfigurationsDropDownForCpo(
        token,
        urlParams
      );
      const data = response?.data?.data;

      if (!Array.isArray(data) || data.length === 0) {
        console.error('No dropdown config data');
        return;
      }
      setDropDownForCpoData(data);
      handleDropDown(data, 'USER_TYPE', userTypeMap, setUserTypes);
    } catch (err) {
      console.error('Failed to fetch dropdown configs:', err);
    }
  };

  useEffect(() => {
    if (Array.isArray(dropDownForCpoData) && dropDownForCpoData?.length > 0) {
      let userTypeMap = getUserTypeData(t).userTypeMap;
      setUserTypeMap(userTypeMap);

      handleDropDown(
        dropDownForCpoData,
        'USER_TYPE',
        userTypeMap,
        setUserTypes
      );
    }
  }, [i18n.language]);

  const handleDropDown = (data, type, labelMap, setDropdown) => {
    const item = data.find((entry) => entry.type === type);
    if (item?.listValues) {
      setDropdown(item.listValues, labelMap);
    }
  };

  const resetFilterApi = () => {
    const resetParams = new URLSearchParams();
    resetParams.set('page', '0');
    resetParams.set('size', pageSize.toString());
    fetchContracts(resetParams);
  };

  useEffect(() => {
    setSelected(selectedRows.map((index) => operatorContract[index]));
  }, [selectedRows]);

  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleClose = () => {
    setAnchorEl(null);
  };

  const [isOpen, setIsOpen] = useState(false);
  const [payload, setPayload] = useState([]);
  const [submit, setSubmit] = useState(0);
  const [showFeedback, setShowFeedback] = useState(false);
  const [showErrorFeedback, setShowErrorFeedback] = useState(false);
  const [showNoEntriesFeedback, setShowNoEntriesFeedback] = useState(false);
  const [showNullBillingStatusFeedback, setShowNullBillingStatusFeedback] =
    useState(false);
  const [
    showMultiNullBillingStatusFeedback,
    setShowMultiNullBillingStatusFeedback,
  ] = useState(false);
  const [checkStatusChange, setCheckStatusChange] = useState(false);

  const [openElem, setOpenElem] = useState(null);
  const [anchorIconButton, setAnchorIconButton] = useState(null);
  const handleCloseIconMenu = () => {
    setOpenElem(null);
    setAnchorIconButton(null);
  };
  const [locations, setLocations] = useState([]);

  function triggerMultipleBillingStatusEdit() {
    if (selectedRows.length > 0) {
      if (selectedRows.filter((row) => row.billingStatus == null).length > 0) {
        setAnchorEl(null);
        setShowMultiNullBillingStatusFeedback(true);
        setSelectedRows([]);
      } else setIsOpen(true);
    } else {
      setAnchorEl(null);
      setShowNoEntriesFeedback(true);
    }
  }

  useEffect(() => {
    api.fetchLocationsForDropDown(token).then((response) => {
      if (response?.data?.data == null) {
        setLocations([]);
      } else {
        setLocations(response?.data?.data);
      }
    });
  }, []);

  const columns = useMemo(() => [
    {
      name: 'nutzerId',
      options: {
        display: false,
        filter: false,
        customBodyRender: (value) => {
          return value || <>-</>;
        },
      },
    },
    {
      name: 'endCustomerId',
      label: t('LBLCustomerNum'),
      options: {
        filter: true,
        filterOptions: {
          fullWidth: true,
        },
        sort: false,
        customFilterListRender: (v) => t('LBLCustomerNum') + `: ${v}`,
        filterType: 'textField',
        customBodyRender: (value) => {
          return <Typography align="left">{value}</Typography>;
        },
        customHeadRender: () => (
          <TableCell
            align="left"
            variant="head"
            sx={{
              textAlign: 'left',
              position: 'sticky',
              top: 0,
              backgroundColor: 'white',
            }}
          >
            {t('LBLCustomerNum')}
          </TableCell>
        ),
      },
    },
    {
      name: 'name',
      label: 'Name',
      options: {
        filter: true,
        filterOptions: {
          fullWidth: true,
        },
        sort: false,
        customFilterListRender: (v) => t('Name') + `: ${v}`,
        filterType: 'textField',
        customBodyRender: (value) => {
          return <Typography align="left">{value}</Typography>;
        },
        customHeadRender: () => (
          <TableCell
            align="left"
            variant="head"
            sx={{
              textAlign: 'left',
              position: 'sticky',
              top: 0,
              backgroundColor: 'white',
            }}
          >
            {t('Name')}
          </TableCell>
        ),
      },
    },
    {
      name: 'location',
      label: t('LBLLocation'),
      options: {
        filter: false,
        customBodyRender: (value) => {
          return <Typography align="left">{value}</Typography>;
        },
        customHeadRender: () => (
          <TableCell
            variant="head"
            sx={{
              textAlign: 'left',
              position: 'sticky',
              top: 0,
              backgroundColor: 'white',
            }}
          >
            {t('LBLLocation')}
          </TableCell>
        ),
      },
    },
    {
      name: 'rfId',
      label: t('LBLRFIDTagRFIDSerialNr'),
      options: {
        display: false,
        filter: true,
        sort: false,
        filterType: 'textField',
        filterOptions: {
          fullWidth: true,
        },
        customFilterListRender: (v) => t('LBLRFIDTagRFIDSerialNr') + `: ${v}`,
      },
    },
    {
      name: 'locationIds',
      label: t('LBLLocation'),
      options: {
        display: false,
        filter: true,
        sort: false,
        filterType: 'multiselect',
        filterOptions: {
          fullWidth: true,
          names: locations.map((x) => x.locationId + ' : ' + x.locationName),
        },
        customFilterListRender: (v) => t('LBLLocation') + `: ${v}`,
      },
    },
    {
      name: 'parkingLotNumbers',
      label: t('LBLParkingLotNum'),
      options: {
        filter: false,
        customBodyRender: (value) => {
          return <Typography align="left">{value.join(', ')}</Typography>;
        },
        customHeadRender: () => (
          <TableCell
            variant="head"
            sx={{
              textAlign: 'left',
              position: 'sticky',
              top: 0,
              backgroundColor: 'white',
            }}
          >
            {t('LBLParkingLotNum')}
          </TableCell>
        ),
      },
    },
    {
      name: 'contractStatusMapping',
      label: t('LBLUserStatus'),
      options: {
        filter: false,
        customBodyRender: (value) => {
          if (!value) return null;
          return (
            <Typography align="center" color={value.colourCode}>
              {t(value.enumName)}
            </Typography>
          );
        },
        customHeadRender: () => (
          <TableCell
            variant="head"
            sx={{
              textAlign: 'center',
              position: 'sticky',
              top: 0,
              backgroundColor: 'white',
            }}
          >
            {t('LBLUserStatus')}
          </TableCell>
        ),
      },
    },
    {
      name: 'details',
      label: ' ',
      options: {
        sort: false,
        filter: false,
        customBodyRender(value) {
          return (
            <>
              <Menu
                anchorEl={anchorIconButton}
                open={openElem === value}
                key={value}
                onClose={handleCloseIconMenu}
                MenuListProps={{
                  'aria-labelledby': 'edit-selected',
                }}
                sx={{
                  textAlign: 'center',
                }}
              >
                <MenuItem
                  key={value}
                  onClick={() => {
                    if (value.billingStatus) {
                      setSelected([value]);
                      setIsOpen(true);
                      handleCloseIconMenu();
                    } else {
                      setShowNullBillingStatusFeedback(true);
                      handleCloseIconMenu();
                    }
                  }}
                >
                  <Typography align="center" sx={{ width: '100%' }}>
                    {t('LBLChangePayment')}
                  </Typography>
                </MenuItem>
              </Menu>
              <Button
                component={NavLink}
                to={`/nutzer/${value.id}/`}
                variant="text"
              >
                Details
              </Button>
            </>
          );
        },
      },
    },
    {
      name: 'userTypes',
      label: t('LBLUserType'),
      options: {
        display: false,
        filter: true,
        sort: false,
        filterType: 'multiselect',
        filterOptions: {
          fullWidth: true,
          names: userTypesForCpoList.map((item) => item.label),
        },
        customFilterListRender: (v) => t('LBLUserType') + `: ${v}`,
      },
    },
  ]);
  useEffect(() => {
    if (enterKeyboardButtonPressed) {
      handleApply();
    }
  }, [enterKeyboardButtonPressed]);

  const handleEnterKey = React.useCallback((e) => {
    console.log('e?.key value ', e?.key);
    if (e?.key === 'Enter') {
      e.preventDefault();
      setEnterKeyboardButtonPressed(true);
    }
  }, []);

  const handleApply = () => {
    if (currentPage > 0) {
      setCurrentPage(0);
    } else {
      getUserData();
    }
    setEnterKeyboardButtonPressed(false);
  };
  return (
    <>
      <LogoLoader loading={fetchingData} />
      <ThemeProvider
        theme={(appTheme) => ({
          ...appTheme,
          components: {
            ...appTheme.components,
            ...dataTableTheme(appTheme).components,
          },
        })}
      >
        <Stack
          alignItems="flex-end"
          spacing={8}
          position="relative"
          width={{
            sm: '100%',
            md: 'calc(100vw - 260px - 40px)',
            lg: 'calc(100vw - 260px - 64px)',
          }}
        >
          <Menu
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            MenuListProps={{
              'aria-labelledby': 'edit-selected',
            }}
            sx={{
              textAlign: 'center',
            }}
          >
            <MenuItem onClick={triggerMultipleBillingStatusEdit}>
              {t('LBLChangePayment')}
            </MenuItem>
          </Menu>
          <Paper
            variant="outlined"
            sx={{
              width: '100%',
              '& .MuiTableCell-head:not(:nth-of-type(2)) button': {
                mx: 0,
              },
            }}
          >
            <DataTable
              data={operatorContract}
              columns={columns}
              options={{
                textLabels: {
                  body: {
                    noMatch: fetchingData ? t('LBLLOading') : t('LBLnoUsers'),
                  },
                },
                onRowSelectionChange: (_props, allRowsSelected) => {
                  setSelectedRows(
                    // eslint-disable-next-line no-unused-vars
                    allRowsSelected.map(({ index, dataIndex }) => dataIndex)
                  );
                },
                rowsSelected: selectedRows,
                filter: true,
                search: false,
                selectableRows: true,
                currentPage: currentPage,
                totalRecords: totalRecords,
                customFilterDialogFooter: () => {
                  return (
                    <div style={{ margin: '1rem', textAlign: 'center' }}>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={handleApply}
                      >
                        {t('BTNSubmit')}
                      </Button>
                    </div>
                  );
                },
                onFilterDialogOpen: () => {
                  window.addEventListener('keydown', handleEnterKey);
                },
                onFilterDialogClose: () => {
                  window.removeEventListener('keydown', handleEnterKey);
                },
                onTableChange: (action, tableState) => {
                  if (action === 'filterChange') {
                    const newFilters = {};
                    tableState.filterList.forEach((filter, index) => {
                      if (filter.length > 0) {
                        newFilters[tableState.columns[index].name] = filter;
                      }
                    });
                    setNewFilters(newFilters);
                  } else if (action === 'resetFilters') {
                    setNewFilters([]);
                    resetFilterApi();
                  }
                },
              }}
              getData={(seletedPageSize, currentPageVal) => {
                setCurrentPage(currentPageVal);
                setPageSize(seletedPageSize);
              }}
            />
          </Paper>
        </Stack>
      </ThemeProvider>
      <StepperPopup
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        title={t('LBLChangePayment')}
        steps={[
          {
            label: t('LBLChangeEnter'),
            component: (
              <MultipleBillingStatusEdit
                data={selected}
                payload={payload}
                setPayload={setPayload}
                submit={submit}
                setShowFeedback={setShowFeedback}
                setShowErrorFeedback={setShowErrorFeedback}
                checkStatusChange={checkStatusChange}
              />
            ),
            nextAction: {
              label: t('LBLFurther'),
              icon: <ArrowForward />,
              action: () => {
                setCheckStatusChange(true);
              },
              canNext:
                !payload.filter((value) => value?.changed === false).length > 0,
            },
          },
          {
            label: t('LBLConfirmChanges'),
            component: (
              <MultipleBillingStatusEdit
                data={selected}
                payload={payload}
                submit={submit}
                setPayload={setPayload}
                setShowFeedback={setShowFeedback}
                setShowErrorFeedback={setShowErrorFeedback}
                isReadOnly
              />
            ),
            nextAction: {
              label: t('LBLSave'),
              icon: <></>,
              action: () => {
                setSubmit((prevState) => prevState + 1);
                setIsOpen(false);
              },
              canNext: false,
            },
          },
        ]}
      />
      <Feedback
        open={showFeedback}
        title={t('LBLPStatusChanged')}
        message={
          <Typography variant="subtitle1" sx={{ width: 400, mx: 10 }}>
            {t('paymentUpdated')}
          </Typography>
        }
        severity="warning"
        handleClose={() => {
          setPayload([]);
          setSelected([]);
          setShowFeedback(false);
        }}
      />
      <Feedback
        open={showErrorFeedback}
        message={
          <Typography
            variant="subtitle1"
            fontWeight={600}
            sx={{ width: 400, mx: 10 }}
          >
            {t('LBLServerAgain')}
          </Typography>
        }
        severity="warning"
        handleClose={() => {
          setPayload([]);
          setSelected([]);
          setShowErrorFeedback(false);
        }}
      />
      <Feedback
        open={showNoEntriesFeedback}
        message={
          <Typography
            variant="subtitle1"
            fontWeight={400}
            sx={{ width: 400, mx: 10 }}
          >
            {t('LBLSelectOneUser')}
          </Typography>
        }
        severity="warning"
        handleClose={() => {
          setPayload([]);
          setSelected([]);
          setShowNoEntriesFeedback(false);
        }}
      />
      <Feedback
        open={showNullBillingStatusFeedback}
        title={t('LBLWrongSelection')}
        message={
          <Typography
            variant="subtitle1"
            fontWeight={400}
            sx={{ width: 400, mx: 10 }}
          >
            {t('LBLNoInvoice')}
          </Typography>
        }
        severity="warning"
        handleClose={() => {
          setPayload([]);
          setSelected([]);
          setShowNullBillingStatusFeedback(false);
        }}
      />
      <Feedback
        open={showMultiNullBillingStatusFeedback}
        title={t('LBLWrongSelection')}
        message={
          <Typography
            variant="subtitle1"
            fontWeight={400}
            sx={{ width: 400, mx: 10 }}
          >
            {t('LBLNoInvoiceUser')}
          </Typography>
        }
        severity="warning"
        handleClose={() => {
          setPayload([]);
          setSelected([]);
          setShowMultiNullBillingStatusFeedback(false);
        }}
      />
    </>
  );
}

function Users() {
  const { i18n, t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [apiCallCount, setApiCallCount] = useState(0);
  const openModal = () => {
    handleResetcount();
    setOpen(true);
  };

  const closeModal = () => {
    setApiCallCount(1);
    setOpen(false);
  };

  const handleResetcount = () => {
    setApiCallCount(0);
  };
  return (
    <React.Fragment>
      <Helmet title="Nutzers" />

      <Grid justifyContent="space-between" container spacing={10}>
        <Grid item>
          <Typography variant="h3" gutterBottom display="inline">
            {t('LBLUserMgmt')}
          </Typography>
        </Grid>
        <Grid item mb={6}>
          <div>
            <Button variant="contained" color="primary" onClick={openModal}>
              <AddIcon />
              {t('LBLCreateUser')}
            </Button>
            <NutzerAnlegen
              paddingLeft={10}
              style={{ backgroundColor: 'red' }}
              open={open}
              handleClose={closeModal}
              setOpen={setOpen}
            />
          </div>
        </Grid>
      </Grid>
      <Divider my={6} />
      <Grid container spacing={6}>
        <Grid item xs={12}>
          <EnhancedTable
            apiCallCount={apiCallCount}
            handleReset={handleResetcount}
          />
        </Grid>
      </Grid>
    </React.Fragment>
  );
}

export default Users;
